<template>
  <Drawer
    v-model:visible="isVisible"
    position="right"
    :style="{ width: '32rem' }"
    :show-close-icon="false"
    :pt-options="{ mergeProps: true }"
    :pt="{ content: { class: 'px-6' } }"
  >
    <template #header>
      <div class="w-full flex justify-between items-center">
        <h2 class="text-xl font-semibold">Change payment method</h2>
        <button
          class="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
          @click="isVisible = false"
        >
          <i class="pi pi-times text-xl" />
        </button>
      </div>
    </template>

    <div v-if="isLoadingMethods" class="flex justify-center p-8">
      <ProgressSpinner />
    </div>

    <div v-else-if="methodsError" class="p-4">
      <Message severity="error">Failed to load payment methods</Message>
    </div>

    <div v-else class="space-y-6 pt-6">
      <Message severity="info" :closable="false" class="text-sm">
        This updates the card used for all upcoming payments on this plan.
        Payments that have already been processed aren't affected.
      </Message>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Payment method
        </label>
        <Select
          :model-value="selectedMethodId"
          :options="selectOptions"
          option-label="name"
          option-value="id"
          option-disabled="isExpired"
          placeholder="Select a payment method"
          class="w-full"
          :pt="paymentMethodSelectPt"
          :pt-options="{ mergeProps: true }"
          @update:model-value="onPaymentMethodChange"
        >
          <template #option="slotProps">
            <StoredPaymentMethodOptionContent
              v-if="slotProps.option.data"
              :name="slotProps.option.name"
              :expiry="slotProps.option.expiry"
              :is-expired="slotProps.option.isExpired"
              :is-last-used="slotProps.option.isLastUsed"
            />
            <span
              v-else
              :class="{
                'text-blue-600 font-medium':
                  slotProps.option.id === STORED_PAYMENT_METHOD_ADD_NEW,
              }"
              >{{ slotProps.option.name }}</span
            >
          </template>
        </Select>
      </div>

      <Button
        label="Change method"
        :loading="isSubmitting"
        :disabled="!canSubmit"
        class="w-full"
        @click="handleSubmit"
      />
    </div>
  </Drawer>

  <PayabliPaymentDrawer
    v-if="showPayabliDrawer"
    :visible="showPayabliDrawer"
    :amount="0"
    payment-option="one-time"
    :api-token="apiToken"
    :entry-point-name="entryPointName"
    :customer-number="payabliCustomerNumber"
    @update:visible="showPayabliDrawer = $event"
    @success="handleNewMethodSuccess"
    @cancel="showPayabliDrawer = false"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Drawer from "primevue/drawer";
import Select from "primevue/select";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import Message from "primevue/message";
import PayabliPaymentDrawer from "./PayabliPaymentDrawer.vue";
import StoredPaymentMethodOptionContent from "./StoredPaymentMethodOptionContent.vue";
import { useContactPaymentMethods } from "../composables/useContactPaymentMethods";
import { useChangePaymentMethod } from "../composables/useChangePaymentMethod";
import { usePaymentSettings } from "../composables/usePaymentSettings";
import { useToast } from "primevue/usetoast";
import {
  STORED_PAYMENT_METHOD_ADD_NEW,
  STORED_PAYMENT_METHOD_ADD_NEW_LABEL,
} from "@/modules/payments/constants";
import {
  isExpiredSelection,
  paymentMethodSelectPt,
} from "@/modules/payments/utils/storedPaymentMethodOption";
import { PaymentMethodTypeEnum } from "@/api/model";

interface Props {
  paymentId: string;
  associatedContactId: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  success: [];
}>();

const toast = useToast();

const isVisible = defineModel<boolean>("visible", { required: true });

const {
  isLoading: isLoadingMethods,
  error: methodsError,
  paymentMethodOptions,
  payabliCustomerNumber,
  fetchContactPaymentMethods,
} = useContactPaymentMethods();

const { isLoading: isSubmitting, changePaymentMethod } =
  useChangePaymentMethod();

const { paymentSettings, fetchPaymentSettings } = usePaymentSettings();

const apiToken = computed(
  () => paymentSettings.value?.public_payabli_api_key || ""
);
const entryPointName = computed(
  () => paymentSettings.value?.payabli_entry_name || ""
);

const selectedMethodId = ref<string | null>(null);
const showPayabliDrawer = ref(false);

const selectOptions = computed(() => [
  ...paymentMethodOptions.value,
  {
    id: STORED_PAYMENT_METHOD_ADD_NEW,
    name: STORED_PAYMENT_METHOD_ADD_NEW_LABEL,
    type: "",
    expiry: null,
    isExpired: false,
    isLastUsed: false,
  },
]);

const selectedOption = computed(() =>
  paymentMethodOptions.value.find((m) => m.id === selectedMethodId.value)
);

const canSubmit = computed(() => {
  return (
    selectedMethodId.value !== null &&
    selectedMethodId.value !== STORED_PAYMENT_METHOD_ADD_NEW &&
    !selectedOption.value?.isExpired &&
    !isSubmitting.value
  );
});

watch(
  isVisible,
  (visible) => {
    if (visible && props.associatedContactId) {
      selectedMethodId.value = null;
      fetchContactPaymentMethods(props.associatedContactId);
      fetchPaymentSettings();
    }
  },
  { immediate: true }
);

// The Select is bound one-way so an expired row (which PrimeVue still lets
// the mouse pick despite option-disabled) never reaches the model.
const onPaymentMethodChange = (value: string | null) => {
  if (isExpiredSelection(selectOptions.value, value)) return;
  if (value === STORED_PAYMENT_METHOD_ADD_NEW) {
    selectedMethodId.value = null;
    showPayabliDrawer.value = true;
    return;
  }
  selectedMethodId.value = value;
};

const handleNewMethodSuccess = async (paymentReference: string) => {
  showPayabliDrawer.value = false;

  await fetchContactPaymentMethods(props.associatedContactId);

  selectedMethodId.value = paymentReference;
};

const getSelectedMethodType = (): PaymentMethodTypeEnum => {
  const type = selectedOption.value?.type;
  if (type === PaymentMethodTypeEnum.ach) return PaymentMethodTypeEnum.ach;
  return PaymentMethodTypeEnum.card;
};

const handleSubmit = async () => {
  if (!selectedMethodId.value) return;

  const success = await changePaymentMethod({
    scheduledPaymentId: props.paymentId,
    paymentMethodToken: selectedMethodId.value,
    paymentMethodType: getSelectedMethodType(),
  });

  if (success) {
    toast.add({
      severity: "success",
      summary: "Payment method updated",
      life: 3000,
    });
    isVisible.value = false;
    emit("success");
  } else {
    toast.add({
      severity: "error",
      summary: "Failed to update payment method",
      life: 5000,
    });
  }
};
</script>
